// src/WhatToEat.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import foodData from '../data/foodData.json';
import { fetchUserCustomFoods, saveUserCustomFoods } from '../FirebaseService';
import './WhatToEat.css';

const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?auto=format&fit=crop&w=500&q=80';
const SLIDER_MIN = 0;
const SLIDER_MAX = 100000;
const SLIDER_STEP = 5000;

// ====================================================================
// HỆ THỐNG XỬ LÝ ÂM THANH CHUYÊN SÂU (WEB AUDIO API - ĐẦM & ẤM)
// ====================================================================
let audioCtx = null;
let audioBuffer = null;

const getAudioContext = () => {
  if (!audioCtx) {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (AudioContextClass) {
      audioCtx = new AudioContextClass();
    }
  }
  if (audioCtx && audioCtx.state === 'suspended') {
    audioCtx.resume();
  }
  return audioCtx;
};

// Nạp trước buffer âm thanh vào RAM để phát tức thì (0ms latency)
const preloadAudioBuffer = async () => {
  if (audioBuffer) return audioBuffer;
  const ctx = getAudioContext();
  if (!ctx) return null;

  const candidateUrls = [
    '/sounds/sounds_scroll.wav',
    '/sounds/csgo_ui_crate_item_scroll.wav'
  ];

  for (const url of candidateUrls) {
    try {
      const res = await fetch(url);
      if (res.ok) {
        const arrayBuffer = await res.arrayBuffer();
        audioBuffer = await ctx.decodeAudioData(arrayBuffer);
        return audioBuffer;
      }
    } catch (e) {
      // Thử tiếp link dự phòng
    }
  }
  return null;
};

// Phát tiếng tick với hiệu ứng làm ĐẦM, CHẮC & TRẦM
const playDeepTick = () => {
  try {
    const ctx = getAudioContext();
    if (!ctx || !audioBuffer) {
      // Dự phòng nếu Web Audio chưa sẵn sàng
      const fallback = new Audio('/sounds/sounds_scroll.wav');
      fallback.playbackRate = 0.84;
      fallback.volume = 0.45;
      fallback.play().catch(() => {});
      return;
    }

    const source = ctx.createBufferSource();
    source.buffer = audioBuffer;

    // 1. Giảm tốc độ phát (hạ pitch) để tiếng dày và trầm hơn
    source.playbackRate.value = 0.84;

    // 2. Bộ lọc Lowpass: Cắt bỏ dải âm cao the thé (>2800Hz), triệt tiêu tiếng đanh chát
    const lowpass = ctx.createBiquadFilter();
    lowpass.type = 'lowpass';
    lowpass.frequency.value = 2800;
    lowpass.Q.value = 1.0;

    // 3. Tăng cường âm Bass (+4.5dB ở dải 220Hz) giúp tiếng tick có độ nặng và đầm chắc
    const bass = ctx.createBiquadFilter();
    bass.type = 'lowshelf';
    bass.frequency.value = 220;
    bass.gain.value = 4.5;

    // 4. Kiểm soát âm lượng
    const gainNode = ctx.createGain();
    gainNode.gain.value = 0.45;

    // Kết nối chuỗi xử lý âm thanh
    source.connect(lowpass);
    lowpass.connect(bass);
    bass.connect(gainNode);
    gainNode.connect(ctx.destination);

    source.start(0);
  } catch (err) {
    // Tránh gián đoạn giao diện nếu có lỗi âm thanh
  }
};

const WhatToEat = () => {
  const navigate = useNavigate();
  const tabs = foodData.tabs || [];
  const allCategories = foodData.categories || [];
  const baseFoods = useMemo(() => foodData.foods || [], []);

  // 1. NHẬN DIỆN ĐỊNH DANH NGƯỜI DÙNG
  const currentUser = useMemo(() => {
    const isGLV = localStorage.getItem('isLoggedIn') === 'true' && localStorage.getItem('uid');
    if (isGLV) {
      const holy = localStorage.getItem('catechistHolyName') || '';
      const name = localStorage.getItem('catechistFullName') || 'Giáo lý viên';
      return {
        role: 'catechists',
        id: localStorage.getItem('uid'),
        name: `${holy} ${name} (GLV)`.trim(),
        badge: '👨‍🏫 Giáo Lý Viên'
      };
    }

    const studentId = localStorage.getItem('studentId');
    if (studentId) {
      const holy = localStorage.getItem('studentHolyName') || '';
      const name = localStorage.getItem('studentFullName') || studentId;
      return {
        role: 'students',
        id: studentId,
        name: `${holy} ${name} (Thiếu Nhi)`.trim(),
        badge: '🎒 Thiếu Nhi'
      };
    }

    let guestId = localStorage.getItem('guest_device_id');
    if (!guestId) {
      guestId = `guest_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
      localStorage.setItem('guest_device_id', guestId);
    }
    return {
      role: 'guests',
      id: guestId,
      name: 'Khách tham quan',
      badge: '✨ Khách'
    };
  }, []);

  const [customFoods, setCustomFoods] = useState([]);
  const [activeTab, setActiveTab] = useState('main');
  const currentTabCategories = allCategories.filter((cat) => cat.tab === activeTab);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(80000);

  const [isSpinning, setIsSpinning] = useState(false);
  const [winnerFood, setWinnerFood] = useState(null);
  const [showWinnerModal, setShowWinnerModal] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);

  // Form thêm món mới
  const [newFoodName, setNewFoodName] = useState('');
  const [newFoodTab, setNewFoodTab] = useState('main');
  const [newFoodCategory, setNewFoodCategory] = useState('com');
  const [newFoodPrice, setNewFoodPrice] = useState(30000);
  const [newFoodTag, setNewFoodTag] = useState('Dinh Dưỡng');
  const [newFoodImage, setNewFoodImage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Băng chuyền roulette
  const [reelList, setReelList] = useState([]);
  const [reelStyle, setReelStyle] = useState({
    transform: 'translateX(0px)',
    transition: 'none'
  });

  const containerRef = useRef(null);
  const trackRef = useRef(null);

  // Quản lý đồng bộ chuyển động hình ảnh và âm thanh
  const animFrameRef = useRef(null);
  const lastCrossedIndexRef = useRef(-1);

  // Nạp trước âm thanh khi trang khởi động
  useEffect(() => {
    preloadAudioBuffer();
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, []);

  // 2. TẢI DỮ LIỆU TỪ FIRESTORE
  useEffect(() => {
    let isMounted = true;
    const loadUserData = async () => {
      const cached = localStorage.getItem(`user_foods_${currentUser.role}_${currentUser.id}`);
      if (cached) {
        try {
          setCustomFoods(JSON.parse(cached));
        } catch (e) {
          console.error(e);
        }
      }

      const remoteFoods = await fetchUserCustomFoods(currentUser.role, currentUser.id);
      if (isMounted) {
        setCustomFoods(remoteFoods);
        localStorage.setItem(`user_foods_${currentUser.role}_${currentUser.id}`, JSON.stringify(remoteFoods));
      }
    };

    loadUserData();
    return () => { isMounted = false; };
  }, [currentUser]);

  const allFoods = useMemo(() => {
    return [...customFoods, ...baseFoods];
  }, [customFoods, baseFoods]);

  useEffect(() => {
    const catsInTab = allCategories.filter((c) => c.tab === activeTab).map((c) => c.key);
    setSelectedCategories(catsInTab);
    setWinnerFood(null);
    setShowWinnerModal(false);
  }, [activeTab]);

  const availableFoods = useMemo(() => {
    return allFoods.filter((item) => {
      const matchTab = item.tab === activeTab;
      const matchCategory = selectedCategories.includes(item.category);
      const itemPrice = Number(item.price) || 0;
      const matchPrice = itemPrice >= minPrice && itemPrice <= maxPrice;
      return matchTab && matchCategory && matchPrice;
    });
  }, [allFoods, activeTab, selectedCategories, minPrice, maxPrice]);

  useEffect(() => {
    if (availableFoods.length === 0) {
      setReelList([]);
      return;
    }
    const initialList = [];
    for (let i = 0; i < 20; i++) {
      initialList.push(availableFoods[i % availableFoods.length]);
    }
    setReelList(initialList);
    setReelStyle({
      transform: 'translateX(0px)',
      transition: 'none'
    });
  }, [availableFoods]);

  const handleMinSliderChange = (e) => {
    const value = Math.min(Number(e.target.value), maxPrice - SLIDER_STEP);
    setMinPrice(value);
  };

  const handleMaxSliderChange = (e) => {
    const value = Math.max(Number(e.target.value), minPrice + SLIDER_STEP);
    setMaxPrice(value);
  };

  const handleCheckboxChange = (categoryKey) => {
    if (isSpinning) return;
    setSelectedCategories((prev) => {
      if (prev.includes(categoryKey)) {
        if (prev.length === 1) return prev;
        return prev.filter((k) => k !== categoryKey);
      }
      return [...prev, categoryKey];
    });
  };

  const handleSelectAllCategories = () => {
    if (isSpinning) return;
    const allKeys = currentTabCategories.map((c) => c.key);
    if (selectedCategories.length === allKeys.length) {
      setSelectedCategories([allKeys[0]]);
    } else {
      setSelectedCategories(allKeys);
    }
  };

  // 3. ĐỒNG BỘ ÂM THANH THEO TỪNG THẺ VƯỢT KIM THỜI GIAN THỰC (REALTIME FRAME SYNC)
  const startRealtimeSoundSync = (totalStep) => {
    if (!containerRef.current || !trackRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const pinX = containerRect.left + containerRect.width / 2;

    const initialTrackLeft = trackRef.current.getBoundingClientRect().left;
    lastCrossedIndexRef.current = Math.floor((pinX - initialTrackLeft) / totalStep);

    const monitorScroll = () => {
      if (!trackRef.current || !containerRef.current) return;

      const currentTrackLeft = trackRef.current.getBoundingClientRect().left;
      const currentCrossedIndex = Math.floor((pinX - currentTrackLeft) / totalStep);

      // Khi phát hiện mép một thẻ mới vừa vượt qua kim chỉ định
      if (currentCrossedIndex > lastCrossedIndexRef.current) {
        playDeepTick();
        lastCrossedIndexRef.current = currentCrossedIndex;
      }

      animFrameRef.current = requestAnimationFrame(monitorScroll);
    };

    animFrameRef.current = requestAnimationFrame(monitorScroll);
  };

  const stopRealtimeSoundSync = () => {
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
  };

  // 4. BẮT ĐẦU QUAY BĂNG CHUYỀN (ĐÃ TỐI ƯU TỐC ĐỘ ÊM & CHỐNG LAG)
  const handleSpin = () => {
    if (isSpinning || availableFoods.length === 0) return;

    // Đánh thức AudioContext khi có tương tác người dùng
    getAudioContext();

    setIsSpinning(true);
    setWinnerFood(null);
    setShowWinnerModal(false);

    // 🌟 GIẢM TẢI: Giảm từ 55 thẻ xuống 36 thẻ để nhẹ RAM/GPU cho máy yếu
    const totalCards = 36;
    // 🌟 RÚT NGẮN QUÃNG ĐƯỜNG: Vị trí trúng chuyển từ 42 về 25
    const winningIndex = 25; 
    const generatedList = [];

    for (let i = 0; i < totalCards; i++) {
      const randomItem = availableFoods[Math.floor(Math.random() * availableFoods.length)];
      generatedList.push({ ...randomItem, reelId: `${i}-${Date.now()}` });
    }

    const targetWinner = generatedList[winningIndex];
    setReelList(generatedList);

    // Reset dải thẻ về điểm xuất phát
    setReelStyle({
      transform: 'translateX(0px)',
      transition: 'none'
    });

    requestAnimationFrame(() => {
      setTimeout(() => {
        if (!containerRef.current || !trackRef.current) return;

        const containerWidth = containerRef.current.offsetWidth;
        const firstCard = trackRef.current.children[0];
        if (!firstCard) return;

        const cardWidth = firstCard.offsetWidth;
        const cardGap = 16;
        const totalStep = cardWidth + cardGap;

        const centerViewport = containerWidth / 2 - cardWidth / 2;
        // Độ lệch ngẫu nhiên nhẹ trong phạm vi thẻ
        const microOffset = (Math.random() - 0.5) * (cardWidth * 0.35);
        const destinationX = -(winningIndex * totalStep) + centerViewport + microOffset;

        // 🌟 TĂNG THỜI GIAN: Chạy trong 6.5s để quay đầm và không bị giật khung hình
        const spinDuration = 6.5; // (giây)

        setReelStyle({
          transform: `translateX(${destinationX}px)`,
          transition: `transform ${spinDuration}s cubic-bezier(0.16, 0.85, 0.25, 1)`
        });

        // BẮT ĐẦU ĐO FRAME KHỚP HÌNH & TIẾNG REALTIME
        startRealtimeSoundSync(totalStep);

        // Chờ chạy xong toàn bộ animation
        setTimeout(() => {
          stopRealtimeSoundSync();
          setWinnerFood(targetWinner);
          setIsSpinning(false);
          setShowWinnerModal(true);
        }, spinDuration * 1000 + 80);
      }, 50);
    });
  };

  const formatVND = (num) => {
    return new Intl.NumberFormat('vi-VN').format(num) + 'đ';
  };

  // Thêm món vào Firestore & LocalStorage
  const handleAddFood = async (e) => {
    e.preventDefault();
    if (!newFoodName.trim() || isSubmitting) return;

    setIsSubmitting(true);
    const newFoodItem = {
      id: Date.now(),
      name: newFoodName.trim(),
      tab: newFoodTab,
      category: newFoodCategory,
      price: Number(newFoodPrice) || 20000,
      tag: newFoodTag,
      image: newFoodImage.trim() || FALLBACK_IMAGE,
      isCustom: true
    };

    const updatedCustomFoods = [newFoodItem, ...customFoods];

    setCustomFoods(updatedCustomFoods);
    localStorage.setItem(`user_foods_${currentUser.role}_${currentUser.id}`, JSON.stringify(updatedCustomFoods));

    await saveUserCustomFoods(currentUser.role, currentUser.id, {
      creatorName: currentUser.name,
      foods: updatedCustomFoods
    });

    setNewFoodName('');
    setNewFoodImage('');
    setIsSubmitting(false);
  };

  // Xóa món riêng
  const handleDeleteFood = async (id) => {
    const updated = customFoods.filter((item) => item.id !== id);
    setCustomFoods(updated);
    localStorage.setItem(`user_foods_${currentUser.role}_${currentUser.id}`, JSON.stringify(updated));

    await saveUserCustomFoods(currentUser.role, currentUser.id, {
      creatorName: currentUser.name,
      foods: updated
    });
  };

  // Khôi phục thực đơn gốc
  const handleResetFoods = async () => {
    if (window.confirm('Khôi phục thực đơn mặc định (xóa hết các món bạn đã tự tạo)?')) {
      setCustomFoods([]);
      localStorage.removeItem(`user_foods_${currentUser.role}_${currentUser.id}`);
      await saveUserCustomFoods(currentUser.role, currentUser.id, {
        creatorName: currentUser.name,
        foods: []
      });
    }
  };

  const minSliderPercent = ((minPrice - SLIDER_MIN) / (SLIDER_MAX - SLIDER_MIN)) * 100;
  const maxSliderPercent = ((maxPrice - SLIDER_MIN) / (SLIDER_MAX - SLIDER_MIN)) * 100;

  return (
    <div className="kids-food-wrapper">
      <Navbar />

      <div className="kids-bg-blob kids-blob-1"></div>
      <div className="kids-bg-blob kids-blob-2"></div>
      <div className="kids-bg-blob kids-blob-3"></div>

      <div className="kids-food-container">
        
        {/* THANH TOP BAR: NÚT QUAY VỀ & HUY HIỆU */}
        <div className="kids-top-identity-bar">
          <button className="kids-back-btn" onClick={() => navigate('/')}>
            ⬅ Về Trang Chủ
          </button>
          <div className="kids-user-badge">
            <span>{currentUser.badge}</span>
            <strong>{currentUser.name}</strong>
          </div>
        </div>

        <header className="kids-header">
          <span className="kids-pill-badge">✨ Góc Ẩm Thực Thiếu Nhi ✨</span>
          <h1 className="kids-main-title">Hôm Nay Chúng Mình Ăn Gì?</h1>
          <p className="kids-sub-title">
            Trượt chọn nhóm món và khoảng giá phù hợp rồi bấm quay để chọn món ngon nhé!
          </p>
        </header>

        {/* 1. THANH TABS CUỘN NGANG */}
        <nav className="kids-tabs-nav" aria-label="Danh mục món ăn">
          <ul className="kids-tabs-list">
            {tabs.map((t) => (
              <li key={t.id} className="kids-tab-item">
                <button
                  type="button"
                  className={`kids-tab-btn ${activeTab === t.id ? 'active' : ''}`}
                  onClick={() => {
                    if (!isSpinning) setActiveTab(t.id);
                  }}
                  disabled={isSpinning}
                >
                  {t.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* 2. BỘ LỌC CHECKBOX & KHOẢNG GIÁ */}
        <div className="kids-filter-card">
          <div className="kids-filter-section">
            <div className="kids-filter-header">
              <span className="kids-filter-title">🎯 Chọn phân loại món:</span>
              <button
                type="button"
                className="kids-quick-toggle"
                onClick={handleSelectAllCategories}
                disabled={isSpinning}
              >
                {selectedCategories.length === currentTabCategories.length ? 'Bỏ chọn bớt' : 'Chọn tất cả'}
              </button>
            </div>

            <div className="kids-checkbox-grid">
              {currentTabCategories.map((cat) => {
                const isChecked = selectedCategories.includes(cat.key);
                return (
                  <label
                    key={cat.key}
                    className={`kids-checkbox-label ${isChecked ? 'active' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => handleCheckboxChange(cat.key)}
                      disabled={isSpinning}
                      className="kids-checkbox-input"
                    />
                    <span className="kids-check-custom">
                      {isChecked ? '✓' : ''}
                    </span>
                    <span className="kids-cat-text">{cat.label}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* THANH TRƯỢT 2 ĐẦU */}
          <div className="kids-price-filter-section">
            <div className="kids-price-header-row">
              <span className="kids-filter-title">💰 Khoảng giá mong muốn:</span>
              <div className="kids-price-current-badge">
                Từ <strong>{formatVND(minPrice)}</strong> đến <strong>{formatVND(maxPrice)}</strong>
              </div>
            </div>

            <div className="kids-dual-slider-wrapper">
              <div className="kids-slider-track-bg"></div>
              <div
                className="kids-slider-track-highlight"
                style={{
                  left: `${minSliderPercent}%`,
                  width: `${maxSliderPercent - minSliderPercent}%`
                }}
              ></div>

              <input
                type="range"
                min={SLIDER_MIN}
                max={SLIDER_MAX}
                step={SLIDER_STEP}
                value={minPrice}
                onChange={handleMinSliderChange}
                className="kids-range-input slider-thumb-min"
                disabled={isSpinning}
                aria-label="Giá tối thiểu"
              />

              <input
                type="range"
                min={SLIDER_MIN}
                max={SLIDER_MAX}
                step={SLIDER_STEP}
                value={maxPrice}
                onChange={handleMaxSliderChange}
                className="kids-range-input slider-thumb-max"
                disabled={isSpinning}
                aria-label="Giá tối đa"
              />
            </div>

            <div className="kids-slider-ticks">
              <span onClick={() => { setMinPrice(0); setMaxPrice(30000); }}>≤ 30.000đ</span>
              <span onClick={() => { setMinPrice(20000); setMaxPrice(50000); }}>20k - 50k</span>
              <span onClick={() => { setMinPrice(40000); setMaxPrice(80000); }}>40k - 80k</span>
              <span onClick={() => { setMinPrice(SLIDER_MIN); setMaxPrice(SLIDER_MAX); }}>Tất Cả (0 - 100k)</span>
            </div>
          </div>
        </div>

        {/* 3. BĂNG CHUYỀN VIEWPORT */}
        <div className="kids-reel-viewport" ref={containerRef}>
          <div className="kids-center-pin">
            <div className="kids-pin-arrow-down"></div>
            <div className="kids-pin-line"></div>
            <div className="kids-pin-arrow-up"></div>
          </div>

          {availableFoods.length === 0 ? (
            <div className="kids-no-data-hint">
              😢 Không tìm thấy món nào trong tầm giá {formatVND(minPrice)} - {formatVND(maxPrice)}. Hãy kéo rộng thanh trượt giá ra nhé!
            </div>
          ) : (
            <div className="kids-reel-track" ref={trackRef} style={reelStyle}>
              {reelList.map((item, idx) => (
                <div
                  key={item.reelId || idx}
                  className={`kids-item-card cat-${item.category} ${item.isCustom ? 'is-user-custom' : ''}`}
                >
                  <div className="kids-card-top-info">
                    <span className="kids-card-tag">{item.tag || 'Món Ngon'}</span>
                    <span className="kids-card-price">{formatVND(item.price)}</span>
                  </div>

                  <div className="kids-card-img-wrap">
                    <img
                      src={item.image || FALLBACK_IMAGE}
                      alt={item.name}
                      className="kids-card-img"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = FALLBACK_IMAGE;
                      }}
                      loading="lazy"
                    />
                  </div>

                  <div className="kids-card-name-wrap">
                    <p className="kids-card-name">{item.name}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 4. NÚT ĐIỀU KHIỂN */}
        <div className="kids-action-group">
          <button
            className="kids-spin-main-btn"
            onClick={handleSpin}
            disabled={isSpinning || availableFoods.length === 0}
          >
            {isSpinning ? '🎡 ĐANG CHỌN MÓN NGON...' : '🎈 BẤM ĐỂ CHỌN MÓN!'}
          </button>

          <button
            className="kids-manage-btn"
            onClick={() => setShowManageModal(true)}
            disabled={isSpinning}
          >
            ✏️ Quản Lý Menu ({allFoods.length})
          </button>
        </div>

        {/* 5. MODAL KẾT QUẢ KHI KẾT THÚC VÒNG QUAY */}
        {showWinnerModal && winnerFood && (
          <div className="kids-winner-modal-overlay" onClick={() => setShowWinnerModal(false)}>
            <div className="kids-winner-modal-card" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="kids-winner-modal-close"
                onClick={() => setShowWinnerModal(false)}
              >
                ✕
              </button>

              <div className="kids-winner-confetti">🎉 🥳 🍜</div>
              <span className="kids-winner-congrats">Tadaaa! Món ngon hôm nay của bạn là:</span>

              <div className="kids-winner-img-wrap">
                <img
                  src={winnerFood.image || FALLBACK_IMAGE}
                  alt={winnerFood.name}
                  className="kids-winner-img"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = FALLBACK_IMAGE;
                  }}
                />
              </div>

              <h2 className="kids-winner-dish-name">{winnerFood.name}</h2>

              <div className="kids-winner-meta-row">
                <span className="kids-winner-price-badge">{formatVND(winnerFood.price)}</span>
                <span className="kids-winner-tag-badge">{winnerFood.tag}</span>
              </div>

              <p className="kids-winner-note">
                Chúc bạn có một bữa ăn thật ngon miệng, bổ dưỡng và tràn đầy niềm vui nhé!
              </p>

              <div className="kids-winner-actions">
                <button
                  type="button"
                  className="kids-winner-btn-accept"
                  onClick={() => setShowWinnerModal(false)}
                >
                  😋 Chốt Món Này Luôn!
                </button>
                <button
                  type="button"
                  className="kids-winner-btn-respin"
                  onClick={handleSpin}
                >
                  🔄 Đổi Món Khác
                </button>
              </div>
            </div>
          </div>
        )}

        {/* MODAL QUẢN LÝ THỰC ĐƠN */}
        {showManageModal && (
          <div className="kids-modal-overlay" onClick={() => setShowManageModal(false)}>
            <div className="kids-modal-card" onClick={(e) => e.stopPropagation()}>
              <div className="kids-modal-header">
                <div>
                  <h3>Thực Đơn Của Bạn</h3>
                  <small style={{ color: '#64748b' }}>Đồng bộ Firestore: {currentUser.role}/{currentUser.id}</small>
                </div>
                <button className="kids-modal-close-btn" onClick={() => setShowManageModal(false)}>✕</button>
              </div>

              <form onSubmit={handleAddFood} className="kids-add-form">
                <input
                  type="text"
                  placeholder="Nhập tên món ăn mới..."
                  value={newFoodName}
                  onChange={(e) => setNewFoodName(e.target.value)}
                  className="kids-input"
                  maxLength={40}
                  required
                />

                <div className="kids-form-select-row">
                  <select
                    value={newFoodTab}
                    onChange={(e) => {
                      setNewFoodTab(e.target.value);
                      const matchedCats = allCategories.filter((c) => c.tab === e.target.value);
                      if (matchedCats.length > 0) setNewFoodCategory(matchedCats[0].key);
                    }}
                    className="kids-select"
                  >
                    {tabs.map((t) => (
                      <option key={t.id} value={t.id}>{t.label}</option>
                    ))}
                  </select>

                  <select
                    value={newFoodCategory}
                    onChange={(e) => setNewFoodCategory(e.target.value)}
                    className="kids-select"
                  >
                    {allCategories
                      .filter((c) => c.tab === newFoodTab)
                      .map((c) => (
                        <option key={c.key} value={c.key}>{c.label}</option>
                      ))}
                  </select>
                </div>

                <div className="kids-form-select-row">
                  <input
                    type="number"
                    placeholder="Giá tiền (VNĐ)..."
                    value={newFoodPrice}
                    onChange={(e) => setNewFoodPrice(Number(e.target.value))}
                    className="kids-input"
                    step="1000"
                    required
                  />

                  <select
                    value={newFoodTag}
                    onChange={(e) => setNewFoodTag(e.target.value)}
                    className="kids-select"
                  >
                    <option value="Dinh Dưỡng">Dinh Dưỡng</option>
                    <option value="Thơm Ngon">Thơm Ngon</option>
                    <option value="Món Ruột">Món Ruột</option>
                    <option value="Thanh Mát">Thanh Mát</option>
                    <option value="Yêu Thích">Yêu Thích</option>
                  </select>
                </div>

                <input
                  type="url"
                  placeholder="Link hình ảnh món ăn (tuỳ chọn)..."
                  value={newFoodImage}
                  onChange={(e) => setNewFoodImage(e.target.value)}
                  className="kids-input"
                />

                <button type="submit" className="kids-btn-submit-add" disabled={isSubmitting}>
                  {isSubmitting ? 'Đang lưu...' : '➕ Thêm Món Vào Menu Của Bạn'}
                </button>
              </form>

              <div className="kids-items-scroll">
                {customFoods.length > 0 && (
                  <div className="kids-section-subtitle">Món riêng của bạn ({customFoods.length}):</div>
                )}
                {customFoods.map((item) => (
                  <div key={item.id} className="kids-manage-row is-own-food">
                    <img
                      src={item.image || FALLBACK_IMAGE}
                      alt={item.name}
                      className="kids-manage-thumb"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = FALLBACK_IMAGE;
                      }}
                    />
                    <div className="kids-row-info">
                      <span className="kids-row-title">⭐ {item.name}</span>
                      <span className="kids-row-sub">
                        {formatVND(item.price)} • {item.tag}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="kids-row-del"
                      onClick={() => handleDeleteFood(item.id)}
                      title="Xoá món này"
                    >
                      🗑️
                    </button>
                  </div>
                ))}

                <div className="kids-section-subtitle">Món mặc định hệ thống ({baseFoods.length}):</div>
                {baseFoods.map((item) => (
                  <div key={item.id} className="kids-manage-row">
                    <img
                      src={item.image || FALLBACK_IMAGE}
                      alt={item.name}
                      className="kids-manage-thumb"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = FALLBACK_IMAGE;
                      }}
                    />
                    <div className="kids-row-info">
                      <span className="kids-row-title">{item.name}</span>
                      <span className="kids-row-sub">
                        {formatVND(item.price)} • {item.tag}
                      </span>
                    </div>
                    <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Mặc định</span>
                  </div>
                ))}
              </div>

              <div className="kids-modal-footer">
                <button
                  type="button"
                  className="kids-btn-default-reset"
                  onClick={handleResetFoods}
                >
                  Xóa món tự tạo (Khôi phục gốc)
                </button>
                <button className="kids-btn-modal-done" onClick={() => setShowManageModal(false)}>
                  Hoàn Tất
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default WhatToEat;